import React, { useState, useEffect, useRef } from 'react';
import { loadBalance } from '../../../api/cardService';
import axios from 'axios';
import { FaCreditCard, FaCcVisa, FaCheckCircle, FaTimesCircle, FaLock, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './CardApplicationForm.scss';

const CardBalanceLoad = () => {
    // Basic Form State
    const [cardNumber, setCardNumber] = useState('');
    const [subscriptionDuration, setSubscriptionDuration] = useState('1 Ay');
    
    // Dynamic Fees State
    const [subscriptionFees, setSubscriptionFees] = useState({
        '1 Ay': { fee: 600, detail: 'Haftada 2 gün - Toplam 8 seans' },
        '3 Ay': { fee: 1700, detail: 'Haftada 2 gün - Toplam 24 seans' },
        'Kadın Eğitim': { fee: 900, detail: 'Kadın eğitim programı' }
    });
    
    const [amount, setAmount] = useState('600');
    const [loading, setLoading] = useState(false);

    // Fetch dynamic fees on mount
    useEffect(() => {
        const fetchFees = async () => {
            try {
                const baseUrl = process.env.REACT_APP_API_URL || 'https://api.iskenderun.bel.tr/api';
                const [subResponse, womenResponse] = await Promise.all([
                    axios.get(`${baseUrl}/card/subscription-fees`),
                    axios.get(`${baseUrl}/women-training/fee`)
                ]);
                const newFees = {
                    '1 Ay': { fee: 600, detail: 'Haftada 2 gün - Toplam 8 seans' },
                    '3 Ay': { fee: 1700, detail: 'Haftada 2 gün - Toplam 24 seans' },
                    'Kadın Eğitim': { fee: 900, detail: 'Kadın eğitim programı' }
                };
                if (subResponse.data?.isSuccess) {
                    newFees['1 Ay'].fee = subResponse.data.data.fee1Ay;
                    newFees['3 Ay'].fee = subResponse.data.data.fee3Ay;
                }
                if (womenResponse.data?.isSuccess && womenResponse.data.data?.fee) {
                    newFees['Kadın Eğitim'].fee = womenResponse.data.data.fee;
                }
                setSubscriptionFees(newFees);
            } catch (error) {
                console.error("Abonelik ücretleri alınamadı:", error);
            }
        };
        fetchFees();
    }, []);

    // Payment State
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentData, setPaymentData] = useState({
        cardName: '',
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: ''
    });
    const [paymentErrors, setPaymentErrors] = useState({});
    const [paymentHtml, setPaymentHtml] = useState(null);
    const [transactionId, setTransactionId] = useState(null);
    const [iframeLoading, setIframeLoading] = useState(true);

    // Result State
    const [paymentOutcome, setPaymentOutcome] = useState(null); // 'success' | 'failed'
    const [resultMessage, setResultMessage] = useState('');

    const iframeRef = useRef(null);

    // Helpers
    const detectCardType = (number) => {
        const re = {
            1: /^4/,
            2: /^5[1-5]/,
        };
        if (re[1].test(number)) return '1'; // Visa
        if (re[2].test(number)) return '2'; // MasterCard
        return null;
    };

    const getSubFee = (duration) => {
        return subscriptionFees[duration]?.fee || 0;
    };

    const getSubDetail = (duration) => {
        return subscriptionFees[duration]?.detail || '';
    };

    // Update amount when duration or fees change
    useEffect(() => {
        setAmount(getSubFee(subscriptionDuration).toString());
    }, [subscriptionDuration, subscriptionFees]);

    // Payment Result Listener
    useEffect(() => {
        const handleMessage = (event) => {
            // Origin check logic similar to SportsAppointment
            // Simplify for this component
            const data = event.data;
            if (!data || data.type !== 'PAYMENT_RESULT') return;

            if (data.status === 'success') {
                setPaymentOutcome('success');
                setResultMessage('Kart bakiyesi başarıyla yüklendi.');
                setPaymentHtml(null);
            } else if (data.status === 'failed') {
                setPaymentOutcome('failed');
                setResultMessage(data.error ? decodeURIComponent(data.error) : 'Ödeme işlemi başarısız oldu.');
                setPaymentHtml(null);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Iframe Load Handler (Fallback similar to SportsAppointment)
    const handleIframeLoad = () => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        try {
            const href = iframe.contentWindow?.location?.href;
            if (!href) return;

            // Check if returned to our app
            const iframeUrl = new URL(href);
            // Basic check if it contains our success/fail paths or query params
            const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
            const error = iframeUrl.searchParams.get('error');

            if (paymentStatus === 'success') {
                setPaymentOutcome('success');
                setResultMessage('Kart bakiyesi başarıyla yüklendi.');
                setPaymentHtml(null);
            } else if (paymentStatus === 'failed') {
                setPaymentOutcome('failed');
                setResultMessage(error ? decodeURIComponent(error) : 'Ödeme işlemi başarısız oldu.');
                setPaymentHtml(null);
            }
        } catch (err) {
            // CORS error expected when on bank page
        }
    };


    // Step 1: Initialize Load Transaction & Switch to Payment Form
    const handleInitiate = async () => {
        if (!cardNumber || !amount) {
            toast.warn('Lütfen kart numarası ve tutar giriniz.', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
            });
            return;
        }

        setLoading(true);
        try {
            const res = await loadBalance({ cardNumber, amount: parseFloat(amount) });

            if (res.isSuccess) {
                setTransactionId(res.data.transactionId);
                setShowPaymentForm(true);
                setPaymentOutcome(null); // Reset result
                setPaymentHtml(null);
                setPaymentData({
                    cardName: '',
                    cardNumber: '',
                    expiryMonth: '',
                    expiryYear: '',
                    cvv: ''
                });
            } else {
                toast.error(res.resultMessage || "Kart yükleme işlemi başlatılamadı.");
            }
        } catch (error) {
            console.error(error);
            toast.error("İşlem başlatılırken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Submit Payment to Backend
    const handlePaymentSubmit = async () => {
        const errors = {};
        if (!paymentData.cardName) errors.cardName = 'Kart üzerindeki isim gereklidir';
        if (!paymentData.cardNumber || paymentData.cardNumber.replace(/\s/g, '').length < 16) errors.cardNumber = 'Geçerli bir kart numarası giriniz';
        if (!paymentData.expiryMonth || !paymentData.expiryYear) errors.expiry = 'Son kullanma tarihi seçiniz';
        if (!paymentData.cvv || paymentData.cvv.length < 3) errors.cvv = 'CVV gereklidir';

        if (Object.keys(errors).length > 0) {
            setPaymentErrors(errors);
            return;
        }

        setLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_URL || 'https://panel.iskenderun.bel.tr/api';
            // Clean Card Number
            const cleanCardNumber = paymentData.cardNumber.replace(/\s/g, '');

            const payload = {
                amount: parseFloat(amount),
                paymentGroup: "CardTopUp",
                referenceId: transactionId.toString(),
                description: `Kart Yukleme Ref:${transactionId}`,
                callbackUrl: window.location.origin + "/hizmet-rehberi/spor-randevu/balance-callback",
                pan: cleanCardNumber,
                cv2: paymentData.cvv,
                expireMonth: paymentData.expiryMonth,
                expireYear: paymentData.expiryYear,
                cardType: detectCardType(cleanCardNumber) || "1",
                firstName: paymentData.cardName.split(' ')[0],
                lastName: paymentData.cardName.split(' ').slice(1).join(' ') || 'User',
                email: 'info@iskenderun.bel.tr'
            };

            const paymentRes = await axios.post(`${baseUrl}/payment/initiate-general`, payload);

            if (paymentRes.data?.isSuccess) {
                const htmlContent = paymentRes.data.data.html || paymentRes.data.data;
                setPaymentHtml(htmlContent);
                setIframeLoading(true);
            } else {
                // Show Error UI instead of alert
                setPaymentOutcome('failed');
                const paymentResultMessage = paymentRes && paymentRes.data ? paymentRes.data.resultMessage : '';
                setResultMessage(paymentResultMessage || '\u00d6deme ba\u015flat\u0131lamad\u0131.');
            }
        } catch (error) {
            console.error("Payment Error:", error);
            setPaymentOutcome('failed');
            setResultMessage("Ödeme servisinde hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Styles matching SportsAppointment.scss
    const styles = {
        sectionShell: {
        },
        heroWrap: {
            marginBottom: '26px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '14px',
            flexWrap: 'wrap'
        },
        heroTitleWrap: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        heroIcon: {
            width: '46px',
            height: '46px',
            borderRadius: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            background: 'linear-gradient(135deg, #0f75bc 0%, #1d4ed8 100%)',
            boxShadow: '0 8px 18px rgba(29, 78, 216, 0.25)',
            fontSize: '20px'
        },
        title: {
            margin: 0,
            color: '#1e293b',
            fontSize: '1.65rem',
            fontWeight: '750',
            letterSpacing: '-0.01em'
        },
        subtitle: {
            margin: '6px 0 0',
            color: '#64748b',
            fontSize: '0.95rem',
            lineHeight: '1.55'
        },
        secureBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.83rem',
            fontWeight: '700',
            color: '#1e3a8a',
            background: '#dbeafe',
            border: '1px solid #bfdbfe',
            borderRadius: '999px',
            padding: '8px 13px'
        },
        initialCard: {
            background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
            border: '1px solid #dbe3ef',
            borderRadius: '18px',
            padding: '30px 26px',
            boxShadow: '0 10px 22px rgba(15, 23, 42, 0.06)',
            maxWidth: '600px',
            margin: '0 auto',
            width: '100%'
        },
        inputGroup: {
            marginBottom: '18px'
        },
        inputV1: {
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #cbd5e1',
            borderRadius: '12px',
            fontSize: '15px',
            color: '#1f2937',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            background: '#fff'
        },
        label: {
            display: 'block',
            fontSize: '13px',
            fontWeight: '700',
            color: '#334155',
            marginBottom: '8px',
            letterSpacing: '0.01em'
        },
        helper: {
            marginTop: '8px',
            fontSize: '12px',
            color: '#64748b'
        },
        startBtn: {
            marginTop: '8px',
            width: '100%',
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #0f75bc 0%, #0d5a8f 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '12px',
            fontWeight: '700',
            cursor: 'pointer',
            fontSize: '1rem',
            letterSpacing: '0.01em',
            opacity: loading ? 0.7 : 1,
            boxShadow: '0 10px 20px rgba(217, 119, 6, 0.24)'
        },
        submitBtn: {
            width: '100%',
            padding: '16px 24px',
            background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '17px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
        }
    };

    return (
        <div className="sa-card-panel-bg">
            <div className="sa-facility-details-header sa-unified-tab-header">
                <div className="sa-unified-tab-header-row">
                    <h3 className="sa-unified-tab-title">
                        <div className="sa-unified-tab-icon">
                            <FaCreditCard size={20} />
                        </div>
                        {'Bakiye Y\u00fckleme'}
                    </h3>
                    <span className="sa-unified-tab-pill">
                        <FaLock />
                        {'G\u00fcvenli \u00f6deme'}
                    </span>
                </div>
                <p className="sa-unified-tab-subtitle">
                    {'Kart numaran\u0131z\u0131 ve tutar bilgisini girip \u00f6deme ad\u0131m\u0131na ge\u00e7ebilirsiniz.'}
                </p>
            </div>
            {!showPaymentForm ? (
                // VIEW 1: INITIAL LOAD FORM
                <div className="sa-initial-load-form" style={styles.initialCard}>
                    <h4 style={{
                        fontSize: '0.85rem',
                        fontWeight: '700',
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                        Yükleme Bilgileri
                    </h4>
                    <div className="sa-form-grid">
                        <div className="form-group" style={{ ...styles.inputGroup, gridColumn: '1 / -1' }}>
                            <label style={styles.label}>Yüklenecek Kart Numarası</label>
                            <input
                                style={styles.inputV1}
                                value={cardNumber}
                                onChange={e => setCardNumber(e.target.value)}
                                placeholder="Kart numaranızı giriniz"
                                maxLength={20}
                            />
                            {/* Card Number Location Guide */}
                            <div style={{
                                marginTop: '14px',
                                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                border: '1px solid #bae6fd',
                                borderRadius: '14px',
                                padding: '16px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '16px'
                            }}>
                                {/* Card Illustration */}
                                <div style={{ flex: '0 0 auto' }}>
                                    <div style={{
                                        width: '120px',
                                        height: '76px',
                                        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                                        borderRadius: '8px',
                                        border: '1.5px solid #cbd5e1',
                                        position: 'relative',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Card chip notch */}
                                        <div style={{
                                            position: 'absolute',
                                            top: '-1px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '20px',
                                            height: '6px',
                                            background: '#e2e8f0',
                                            borderRadius: '0 0 4px 4px',
                                            border: '1px solid #cbd5e1',
                                            borderTop: 'none'
                                        }}></div>
                                        {/* Number area highlight */}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '6px',
                                            left: '8px',
                                            right: '8px',
                                            height: '16px',
                                            background: 'rgba(59, 130, 246, 0.12)',
                                            border: '1.5px dashed #3b82f6',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            animation: 'cardNumPulse 2s ease-in-out infinite'
                                        }}>
                                            <span style={{
                                                fontSize: '7px',
                                                fontFamily: 'monospace',
                                                color: '#1e40af',
                                                fontWeight: '700',
                                                letterSpacing: '0.5px'
                                            }}>0010599659 161,48363</span>
                                        </div>
                                        {/* Arrow pointing to number */}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '24px',
                                            right: '6px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center'
                                        }}>
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#3b82f6',
                                                animation: 'arrowBounce 1.5s ease-in-out infinite'
                                            }}>↓</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0c4a6e', marginBottom: '4px' }}>
                                        Kart numaranız nerede?
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#0369a1', lineHeight: '1.5' }}>
                                        Kartınızın <strong>arka yüzünde</strong>, alt kısımda basılı olan numara dizisinin <strong>ilk bölümünü</strong> (virgüllü sayıyı değil) giriniz.
                                    </div>
                                </div>
                            </div>
                            <style>{`
                            @keyframes cardNumPulse {
                                0%, 100% { background: rgba(59,130,246,0.08); }
                                50% { background: rgba(59,130,246,0.2); }
                            }
                            @keyframes arrowBounce {
                                0%, 100% { transform: translateY(0); }
                                50% { transform: translateY(3px); }
                            }
                        `}</style>
                        </div>
                        <div className="form-group" style={styles.inputGroup}>
                            <label style={styles.label}>Üyelik Süresi</label>
                            <select
                                style={styles.inputV1}
                                value={subscriptionDuration}
                                onChange={(e) => setSubscriptionDuration(e.target.value)}
                            >
                                <option value="1 Ay">1 Ay - Havuz Üyelik</option>
                                <option value="3 Ay">3 Ay - Havuz Üyelik</option>
                                <option value="Kadın Eğitim">Kadın Eğitim (900 TL)</option>
                            </select>
                            <div style={{ ...styles.helper, color: '#237CB2', fontWeight: '600', marginTop: '8px' }}>
                                {getSubDetail(subscriptionDuration)}
                            </div>
                        </div>
                        {subscriptionDuration === 'Kadın Eğitim' && (
                            <div style={{
                                gridColumn: '1 / -1',
                                padding: '16px 20px',
                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                border: '1px solid #f59e0b',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                marginBottom: '12px'
                            }}>
                                <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>⚠️</span>
                                <div>
                                    <strong style={{ color: '#92400e', display: 'block', marginBottom: '4px' }}>Önemli Bilgilendirme</strong>
                                    <p style={{ color: '#78350f', margin: 0, fontSize: '0.88rem', lineHeight: '1.6' }}>
                                        Bu ödeme yalnızca <strong>kadın eğitim dersleri</strong> içindir. Lütfen buna göre ödeme yapınız.<br />
                                        <strong>Ödeme sonrası iade yapılmamaktadır.</strong>
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="form-group" style={styles.inputGroup}>
                            <label style={styles.label}>Ödenecek Tutar</label>
                            <input
                                style={{ ...styles.inputV1, background: '#f8fafc', fontWeight: 'bold' }}
                                type="text"
                                value={amount + ' TL'}
                                readOnly
                            />
                            <div style={styles.helper}>Seçilen üyelik süresine göre tutar otomatik belirlenir.</div>
                        </div>
                    </div>
                    <button
                        onClick={handleInitiate}
                        disabled={loading}
                        style={styles.startBtn}>
                        {loading ? 'İşleniyor...' : 'Ödemeye Geç'}
                    </button>
                </div>
            ) : (
                // VIEW 2: PAYMENT CONTAINER (INLINE)
                <div className="sa-inline-payment-container" style={{
                    background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
                    borderRadius: '18px',
                    border: '1px solid #dbe3ef',
                    boxShadow: '0 10px 22px rgba(15, 23, 42, 0.06)',
                    overflow: 'hidden',
                    maxWidth: '800px',
                    margin: '0 auto',
                    width: '100%'
                }}>

                    {/* RESULT VIEW */}
                    {paymentOutcome ? (
                        <div style={{ padding: '40px 30px', textAlign: 'center' }}>
                            <div style={{
                                fontSize: '60px',
                                marginBottom: '20px',
                                color: paymentOutcome === 'success' ? '#22c55e' : '#ef4444'
                            }}>
                                {paymentOutcome === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
                            </div>
                            <h2 style={{
                                fontSize: '24px',
                                fontWeight: '700',
                                color: paymentOutcome === 'success' ? '#15803d' : '#b91c1c',
                                marginBottom: '12px'
                            }}>
                                {paymentOutcome === 'success' ? 'Ödeme Başarılı!' : 'Ödeme Başarısız'}
                            </h2>
                            <p style={{
                                fontSize: '16px',
                                color: paymentOutcome === 'success' ? '#166534' : '#991b1b',
                                marginBottom: '30px',
                                maxWidth: '400px',
                                margin: '0 auto 30px'
                            }}>
                                {resultMessage}
                            </p>

                            {paymentOutcome === 'success' && (
                                <div style={{
                                    background: '#f0fdf4',
                                    border: '1px solid #bbf7d0',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginBottom: '30px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#166534', fontWeight: '600' }}>Kart No</span>
                                        <span style={{ color: '#15803d' }}>{cardNumber}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#166534', fontWeight: '600' }}>Yüklenen Tutar</span>
                                        <span style={{ color: '#15803d', fontWeight: 'bold' }}>{amount} TL</span>
                                    </div>
                                    {transactionId && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#166534', fontWeight: '600' }}>İşlem No</span>
                                            <span style={{ color: '#15803d' }}>{transactionId}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                                {paymentOutcome === 'failed' ? (
                                    <>
                                        <button
                                            onClick={() => { setPaymentOutcome(null); setPaymentHtml(null); }}
                                            style={{
                                                padding: '12px 24px',
                                                borderRadius: '10px',
                                                background: '#2563eb',
                                                color: '#fff',
                                                border: 'none',
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Tekrar Dene
                                        </button>
                                        <button
                                            onClick={() => { setShowPaymentForm(false); setPaymentOutcome(null); }}
                                            style={{
                                                padding: '12px 24px',
                                                borderRadius: '10px',
                                                background: '#ef4444',
                                                color: '#fff',
                                                border: 'none',
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            iptal Et
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => { setShowPaymentForm(false); setPaymentOutcome(null); setCardNumber(''); setAmount(''); }}
                                        style={{
                                            padding: '12px 24px',
                                            borderRadius: '10px',
                                            background: '#2563eb',
                                            color: '#fff',
                                            border: 'none',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Yeni İşlem Yap
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : paymentHtml ? (
                        // 3D SECURE IFRAME
                        <div style={{ position: 'relative', height: '600px' }}>
                            <div style={{
                                padding: '16px',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: '#f8fafc'
                            }}>
                                <h4 style={{ margin: 0, fontSize: '16px', color: '#334155' }}>Güvenli Ödeme Ekranı</h4>
                            </div>

                            {iframeLoading && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50px', left: 0, right: 0, bottom: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    background: '#fff',
                                    zIndex: 10
                                }}>
                                    <div className="sa-card-spinner" style={{
                                        width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '15px'
                                    }}></div>
                                    <p style={{ color: '#64748b', fontWeight: '500' }}>Ödeme sayfası yükleniyor...</p>
                                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                                </div>
                            )}

                            <iframe
                                ref={iframeRef}
                                title="3D Secure Payment"
                                srcDoc={paymentHtml}
                                onLoad={() => {
                                    setIframeLoading(false);
                                    handleIframeLoad();
                                }}
                                style={{ width: '100%', height: 'calc(100% - 50px)', border: 'none' }}
                            />
                        </div>
                    ) : (
                        // CREDIT CARD FORM
                        <div style={{ padding: '30px' }}>
                            {/* HEADER */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '24px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <h4 style={{
                                        margin: 0,
                                        fontSize: '0.85rem',
                                        fontWeight: '700',
                                        color: '#94a3b8',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
                                        Kart Bilgileri
                                    </h4>
                                    <button
                                        onClick={() => setShowPaymentForm(false)}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            color: '#6b7280',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        title="Geri Dön"
                                    >
                                        <FaArrowLeft />
                                    </button>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                    gap: '4px'
                                }}>
                                    <div style={{
                                        fontWeight: '700',
                                        color: '#2563eb',
                                        fontSize: '16px',
                                        background: '#eff6ff',
                                        padding: '8px 16px',
                                        borderRadius: '20px'
                                    }}>
                                        Toplam: {amount} TL
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginRight: '8px' }}>
                                        {subscriptionDuration} Üyelik Paketi
                                    </div>
                                </div>
                            </div>

                            <div className="payment-form-content">
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={styles.label}>Kart Numarası</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            placeholder="0000 0000 0000 0000"
                                            maxLength="19"
                                            value={paymentData.cardNumber}
                                            onChange={e => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                const formatted = val.match(/.{1,4}/g)?.join(' ') || val;
                                                if (val.length <= 16) setPaymentData({ ...paymentData, cardNumber: formatted });
                                                if (paymentErrors.cardNumber) setPaymentErrors({ ...paymentErrors, cardNumber: '' });
                                            }}
                                            style={{
                                                ...styles.inputV1,
                                                borderColor: paymentErrors.cardNumber ? '#ef4444' : '#d1d5db',
                                                paddingRight: '50px'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                            onBlur={(e) => e.target.style.borderColor = paymentErrors.cardNumber ? '#ef4444' : '#d1d5db'}
                                        />
                                        <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)' }}>
                                            {detectCardType(paymentData.cardNumber.replace(/\s/g, '')) === '1' ? <FaCcVisa size={28} color="#1a1f71" /> :
                                                detectCardType(paymentData.cardNumber.replace(/\s/g, '')) === '2' ? <FaCreditCard size={28} color="#F79E1B" /> :
                                                    <FaCreditCard size={24} color="#cbd5e1" />}
                                        </div>
                                    </div>
                                    {paymentErrors.cardNumber && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cardNumber}</div>}
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={styles.label}>Kart Üzerindeki İsim</label>
                                    <input
                                        type="text"
                                        placeholder="AD SOYAD"
                                        value={paymentData.cardName}
                                        onChange={e => {
                                            setPaymentData({ ...paymentData, cardName: e.target.value.toUpperCase() });
                                            if (paymentErrors.cardName) setPaymentErrors({ ...paymentErrors, cardName: '' });
                                        }}
                                        style={{
                                            ...styles.inputV1,
                                            borderColor: paymentErrors.cardName ? '#ef4444' : '#d1d5db'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                        onBlur={(e) => e.target.style.borderColor = paymentErrors.cardName ? '#ef4444' : '#d1d5db'}
                                    />
                                    {paymentErrors.cardName && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cardName}</div>}
                                </div>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '32px' }}>
                                    <div style={{ flex: '1 1 200px' }}>
                                        <label style={styles.label}>Son Kullanma Tarihi</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <select
                                                value={paymentData.expiryMonth}
                                                onChange={e => {
                                                    setPaymentData({ ...paymentData, expiryMonth: e.target.value });
                                                    if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                                }}
                                                style={{
                                                    ...styles.inputV1,
                                                    flex: 1,
                                                    borderColor: paymentErrors.expiry ? '#ef4444' : '#d1d5db'
                                                }}
                                            >
                                                <option value="">Ay</option>
                                                {Array.from({ length: 12 }, (_, i) => <option key={i} value={String(i + 1).padStart(2, '0')}>{String(i + 1).padStart(2, '0')}</option>)}
                                            </select>
                                            <select
                                                value={paymentData.expiryYear}
                                                onChange={e => {
                                                    setPaymentData({ ...paymentData, expiryYear: e.target.value });
                                                    if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                                }}
                                                style={{
                                                    ...styles.inputV1,
                                                    flex: 1,
                                                    borderColor: paymentErrors.expiry ? '#ef4444' : '#d1d5db'
                                                }}
                                            >
                                                <option value="">Yıl</option>
                                                {Array.from({ length: 15 }, (_, i) => <option key={i} value={String(2025 + i)}>{2025 + i}</option>)}
                                            </select>
                                        </div>
                                        {paymentErrors.expiry && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.expiry}</div>}
                                    </div>

                                    <div style={{ width: '120px' }}>
                                        <label style={styles.label}>CVV</label>
                                        <input
                                            type="text"
                                            placeholder="123"
                                            maxLength="3"
                                            value={paymentData.cvv}
                                            onChange={e => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if (val.length <= 3) setPaymentData({ ...paymentData, cvv: val });
                                                if (paymentErrors.cvv) setPaymentErrors({ ...paymentErrors, cvv: '' });
                                            }}
                                            style={{
                                                ...styles.inputV1,
                                                borderColor: paymentErrors.cvv ? '#ef4444' : '#d1d5db'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                            onBlur={(e) => e.target.style.borderColor = paymentErrors.cvv ? '#ef4444' : '#d1d5db'}
                                        />
                                        {paymentErrors.cvv && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{paymentErrors.cvv}</div>}
                                    </div>
                                </div>

                                <button
                                    onClick={handlePaymentSubmit}
                                    disabled={loading}
                                    style={styles.submitBtn}
                                    onMouseOver={(e) => {
                                        if (!loading) {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(37, 99, 235, 0.3)';
                                    }}
                                >
                                    {loading ? 'İşleniyor...' : (
                                        <>
                                            Ödemeyi Tamamla <FaCheckCircle />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CardBalanceLoad;
